<script lang="ts">
	import Card from '@smui/card';
	import Checkbox from '@smui/checkbox';
	import FormField from '@smui/form-field';
	import Select, { Option } from '@smui/select';
	import Input from '@smui/textfield';
	import { _ } from 'svelte-i18n';
	import { writable } from 'svelte/store';
	import FieldLoading from '../../../..//shared/components/common/Loading/FieldLoading.svelte';
	import Title from '../../../..//shared/components/common/Title.svelte';
	import ConfirmModal from '../../../../shared/components/common/Modal/ConfirmModal.svelte';
	import { formatDateTime, resetPageIfFiltersChanged } from '../../../../shared/functions';
	import Area from '../../../../shared/models/Area/Area';
	import AziendaEnte from '../../../../shared/models/AziendaEnte/AziendaEnte';
	import FiltroGenerico from '../../../../shared/models/Filtri/FiltroGenerico';
	import Questionario from '../../../../shared/models/Questionario/Questionario';
	import { hasRoleAreaManager, hasRoleCompanyManager, hasRoleSysAdmin, userSession } from '../../../../shared/stores';
	import TableLoading from './../../../..//shared/components/common/Loading/TableLoading.svelte';
	import Pagination from './../../../..//shared/components/common/Table/Pagination.svelte';

	let filters: FiltroGenerico = {
		descrizione: '',
		daPubblicare: true,
		areaId: $hasRoleAreaManager ? $userSession?.areaId : null,
		aziendaEnteId: null,
		visibileAdAziendaEnte: null,
		annoInizioValidita: new Date().getFullYear(),
		pagina: 1,
		elementiPerPagina: 10,
	};

	$: [data, loading, error] = Questionario.getList(filters);

	let [dataAree, loadingAree, errorAree] = $hasRoleSysAdmin ? Area.getList() : [null, writable(false), null];
	let [dataAziendeEnti, loadingAziendeEnti, errorAziendeEnti] = AziendaEnte.getList({ areaId: filters.areaId });
	let selectedArea: Area = new Area();
	let selectedAziendaEnte: AziendaEnte = new AziendaEnte();

	let [dataEliminazione, loadingEliminazione, errorEliminazione] = [null, null, null];
	let openConfirmDeleteDialog: boolean = false;
	let idToDelete: number = 0;

	async function onDelete(id) {
		[dataEliminazione, loadingEliminazione, errorEliminazione] = await Questionario.delete(id);
		if (!$errorEliminazione) {
			[data, loading, error] = Questionario.getList(filters);
		}
	}

	function onResetFilters() {
		filters.descrizione = '';
		selectedArea = new Area();
		filters.areaId = null;
		selectedAziendaEnte = new AziendaEnte();
		filters.aziendaEnteId = null;
		filters.visibileAdAziendaEnte = null;
	}

	let prevFilters = {};

	$: {
		const newPage = resetPageIfFiltersChanged(prevFilters, filters);
		if (newPage !== null) {
			filters.pagina = newPage;
		}
		prevFilters = { ...filters };
	}

	$: {
		if (filters.annoInizioValidita === null) {
			delete filters.annoInizioValidita;
		} else {
			filters.annoInizioValidita = Number(filters.annoInizioValidita);
		}
	}

	function getYears(): number[] {
		const currentYear = new Date().getFullYear();
		const startYear = 2020;
		return Array.from({length: currentYear - startYear + 1}, (_, i) => currentYear - i);
	}
</script>

<ConfirmModal bind:showModal="{openConfirmDeleteDialog}" onConfirm="{() => onDelete(idToDelete)}" />
<Card class="md:px-8 md:py-8" padded>
	<div class="flex justify-between">
		<Title
			title="{$_('page.survey.list.title_companies/entities')}"
			icon="help_outline"
			iconText="{$_('page.survey.list.helper_text_companies/entities_admin')}"
		/>
		<div class="sm:flex-nowrap sm:space-x-4 flex flex-wrap">
			<button title="{$_('global.reset_filters')}" class="btn-white h-14 w-14 sm:h-16 sm:w-16" on:click="{onResetFilters}">
				<span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">restart_alt</span>
			</button>
		</div>
	</div>
	<div class="md:grid-cols-2 lg:grid-cols-3 grid grid-cols-1 gap-8 p-4">
		<Input label="{$_('page.survey.list.description')}" bind:value="{filters.descrizione}" />
		{#if $hasRoleSysAdmin}
			{#if $loadingAree}
				<FieldLoading />
			{:else}
				<Select key="{(area) => `${(area && area.id) || ''}`}" label="{$_('page.survey.list.area')}" bind:value="{selectedArea}">
					{#if $dataAree.response}
						<Option
							value="{{}}"
							on:click="{() => {
								filters.areaId = null;
								filters.aziendaEnteId = null;
							}}"
						/>
						{#each $dataAree.response as area (area.id)}
							{#if area.nomeComune}
								<Option
									value="{area}"
									on:click="{() => {
										filters.areaId = area.id;
										[dataAziendeEnti, loadingAziendeEnti, errorAziendeEnti] = AziendaEnte.getList({ areaId: area.id });
									}}">{area.nomeComune}</Option
								>
							{/if}
						{/each}
					{/if}
				</Select>
			{/if}
		{/if}
		{#if $loadingAziendeEnti}
			<FieldLoading />
		{:else}
			<Select
				disabled="{!filters.areaId}"
				key="{(aziendaEnte) => `${(aziendaEnte && aziendaEnte.id) || ''}`}"
				label="{$_('page.survey.list.company/entity')}"
				bind:value="{selectedAziendaEnte}"
			>
				{#if $dataAziendeEnti && $dataAziendeEnti.response}
					<Option value="{{}}" on:click="{() => (filters.aziendaEnteId = null)}" />
					{#each $dataAziendeEnti.response as aziendaEnte (aziendaEnte.id)}
						{#if aziendaEnte?.nomeAziendaEnte}
							<Option value="{aziendaEnte}" on:click="{() => (filters.aziendaEnteId = aziendaEnte.id)}"
								>{aziendaEnte.nomeAziendaEnte}</Option
							>
						{/if}
					{/each}
				{/if}
			</Select>
		{/if}
		<FormField>
			<Checkbox bind:checked="{filters.visibileAdAziendaEnte}" />
			<span slot="label">{$_('page.survey.list.visible_to_companies/entities')}</span>
		</FormField>
		<Select 
			label="Anno inizio validità" 
			bind:value="{filters.annoInizioValidita}"
			class="filled-select"
			>
			<Option value={null}>Tutti</Option>
			{#each getYears() as year}
				<Option value="{year}">{year}</Option>
			{/each}
		</Select>
	</div>
	{#if $loading}
		<TableLoading />
	{:else}
		<div class="flex flex-col">
			<div class="sm:-mx-6 lg:-mx-8 -my-2 overflow-x-auto">
				<div class="sm:px-6 lg:px-8 inline-block min-w-full py-2 align-middle">
					<div class="overflow-hidden">
						<table class="min-w-full p-0 m-0 text-sm leading-10 border-t-4 border-collapse border-black table-auto">
							<thead>
								<tr class="p-1">
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.survey.list.code')}</th
									>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.survey.list.description')}</th
									>
									{#if $hasRoleSysAdmin}
										<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
											>{$_('page.survey.list.area')}</th
										>
									{/if}
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.survey.list.company/entity')}</th
									>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.survey.list.start_validity')}</th
									>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.survey.list.end_validity')}</th
									>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.survey.list.visible')}</th
									>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">
										Risposte ricevute
									</th>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">
										Percentuale risposte
									</th>
									<th scope="col" class="relative border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('global.actions')}</th
									>
								</tr>
							</thead>
							<tbody>
								{#if $data?.response}
									{#each $data?.response as row}
										<tr class="odd:bg-white even:bg-gray-50 p-1">
											<td class="border-b p-2.5 text-center leading-5">{row.codice ?? ''}</td>
											<td class="border-b p-2.5 text-center leading-5">{row.descrizione ?? ''}</td>
											{#if $hasRoleSysAdmin}
												<td class="border-b p-2.5 text-center leading-5">{row.area?.nomeComune ?? ''}</td>
											{/if}
											<td class="border-b p-2.5 text-center leading-5">{row.aziendaEnte?.nomeAziendaEnte ?? ''}</td>
											<td class="border-b p-2.5 text-center leading-5"
												>{row.dataInizioValidita ? formatDateTime(row.dataInizioValidita) : ''}</td
											>
											<td class="border-b p-2.5 text-center leading-5"
												>{row.dataFineValidita ? formatDateTime(row.dataFineValidita) : ''}</td
											>
											<td class="border-b p-2.5 text-center leading-5"
												><Checkbox disabled bind:checked="{row.isVisibleToAziendaEnte}" /></td
											>
											<td class="border-b p-2.5 text-center leading-5">
												{row.numeroRisposteRicevute} / {row.numeroTotaleLavoratori}
											</td>
											<td class="border-b p-2.5 text-center leading-5">
												{row.percentualeRisposte.toFixed(2)}%
											</td>
											<td class="whitespace-nowrap border-b p-2.5 text-center leading-5">
												<div class="item-center flex justify-center">
													<div>
														<a href="/#/questionario/{row.id}">
															<button class="h-7.5 w-7.5">
																<span class="material-icons-outlined text-base leading-[1.8rem]">launch</span>
															</button>
														</a>
													</div>
													{#if $hasRoleSysAdmin}
														<div class="ml-4 h-7.5 w-7.5">
															<button
																on:click="{() => {
																	idToDelete = row.id;
																	openConfirmDeleteDialog = true;
																}}"
																class="h-7.5 w-7.5"
															>
																<span class="material-icons-outlined text-base leading-[1.8rem]">delete</span>
															</button>
														</div>
													{/if}
													{#if row.url && $hasRoleCompanyManager}
														<div class="ml-4 h-7.5 w-7.5">
															<a href="/#/questionarioreport/{row.id}">
																<button class="h-7.5 w-7.5">
																	<span class="material-icons-outlined text-base leading-[1.8rem]">summarize</span>
																</button>
															</a>
														</div>
													{/if}
												</div>
											</td>
										</tr>
									{/each}
								{/if}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
		<div class="right-0">
			<Pagination bind:current="{filters.pagina}" bind:num_pages="{$data.totalPages}" bind:per_page="{filters.elementiPerPagina}" />
		</div>
	{/if}
</Card>
